import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { QuoteTemplateApiService } from '../services/quote-template-api.service';
import { QuoteTemplateRequest, QuoteTemplateResponse } from '../models/quote-template.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { environment } from '../../../../environments/environment';

interface FileUploadResponse {
  fileId: number;
  publicUrl: string;
}

@Component({
  selector: 'app-quote-template-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent
  ],
  templateUrl: './quote-template-form.component.html'
})
export class QuoteTemplateFormComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  successMessage = '';
  isEditing = false;
  templateId: number | null = null;

  // Logo upload state
  logoFileId: number | null = null;
  logoPreviewUrl: string | null = null;
  logoUploading = false;

  // Template branding preview (PDF) state
  previewing = false;

  private readonly filesApiUrl = `${environment.apiUrl}api/files`;

  // Current template data
  currentTemplate: QuoteTemplateResponse | null = null;

  constructor(
    private fb: FormBuilder,
    private templateApi: QuoteTemplateApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private http: HttpClient
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.templateId = +id;
      this.cargarTemplate(this.templateId);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      logoUrl: ['', Validators.maxLength(500)],
      primaryColor: ['', Validators.maxLength(20)],
      secondaryColor: ['', Validators.maxLength(20)],
      headerText: ['', Validators.maxLength(500)],
      footerText: ['', Validators.maxLength(500)],
      contactInfoText: ['', Validators.maxLength(500)],
      defaultTermsConditions: ['', Validators.maxLength(5000)],
      showLogo: [true],
      showWatermark: [false],
      watermarkText: ['', Validators.maxLength(100)],
      isDefault: [false],
      isActive: [true]
    });
  }

  cargarTemplate(id: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.getTemplateById(id, businessId).subscribe({
      next: (template: QuoteTemplateResponse) => {
        this.currentTemplate = template;
        this.logoFileId = template.logoFileId ?? null;
        this.logoPreviewUrl = template.logoUrl || null;
        this.form.patchValue({
          name: template.name,
          logoUrl: template.logoUrl || '',
          primaryColor: template.primaryColor || '',
          secondaryColor: template.secondaryColor || '',
          headerText: template.headerText || '',
          footerText: template.footerText || '',
          contactInfoText: template.contactInfoText || '',
          defaultTermsConditions: template.defaultTermsConditions || '',
          showLogo: template.showLogo,
          showWatermark: template.showWatermark,
          watermarkText: template.watermarkText || '',
          isDefault: template.isDefault,
          isActive: template.isActive
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la plantilla';
        this.loading = false;
      }
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.error = '';
    this.successMessage = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const templateRequest = this.buildTemplateRequest();

    if (this.isEditing && this.templateId) {
      this.templateApi.updateTemplate(this.templateId, businessId, templateRequest).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Plantilla de cotización');
          this.router.navigate(['/cotizaciones/plantillas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la plantilla';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar la plantilla' });
        }
      });
    } else {
      this.templateApi.createTemplate(businessId, templateRequest).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Plantilla de cotización');
          this.router.navigate(['/cotizaciones/plantillas']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la plantilla';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear la plantilla' });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/cotizaciones/plantillas']);
  }

  // Upload the logo to storage; the returned fileId is embedded in the PDF, the publicUrl is kept for preview.
  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.logoUploading = true;
    const formData = new FormData();
    formData.append('file', file);

    this.http.post<FileUploadResponse>(
      `${this.filesApiUrl}/upload?category=COMPANY_LOGO&isPublic=true`,
      formData
    ).subscribe({
      next: (res) => {
        this.logoFileId = res.fileId;
        this.logoPreviewUrl = res.publicUrl;
        this.form.patchValue({ logoUrl: res.publicUrl });
        input.value = '';
        // En modo edición, persistir el logo en la plantilla apenas se sube (sin esperar a "Guardar").
        if (this.isEditing && this.templateId) {
          this.persistLogoChange();
        } else {
          this.logoUploading = false;
        }
      },
      error: (err) => {
        this.logoUploading = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo subir el logo. Intenta nuevamente.' });
        input.value = '';
      }
    });
  }

  removeLogo(): void {
    this.logoFileId = null;
    this.logoPreviewUrl = null;
    this.form.patchValue({ logoUrl: '' });
    // En modo edición, persistir la eliminación del logo de inmediato.
    if (this.isEditing && this.templateId) {
      this.persistLogoChange();
    }
  }

  // Construye el request a partir del estado actual del formulario + logo.
  private buildTemplateRequest(): QuoteTemplateRequest {
    const formValue = this.form.value;
    return {
      name: formValue.name,
      logoUrl: formValue.logoUrl || undefined,
      logoFileId: this.logoFileId ?? undefined,
      primaryColor: formValue.primaryColor || undefined,
      secondaryColor: formValue.secondaryColor || undefined,
      headerText: formValue.headerText || undefined,
      footerText: formValue.footerText || undefined,
      contactInfoText: formValue.contactInfoText || undefined,
      defaultTermsConditions: formValue.defaultTermsConditions || undefined,
      showLogo: formValue.showLogo,
      showWatermark: formValue.showWatermark,
      watermarkText: formValue.watermarkText || undefined,
      isDefault: formValue.isDefault,
      isActive: formValue.isActive
    };
  }

  // Guarda SOLO el cambio de logo (solo en modo edición), sin arrastrar otros cambios del
  // formulario aún no guardados: parte de los valores ya persistidos (currentTemplate) y
  // sobreescribe únicamente logoFileId/logoUrl.
  private persistLogoChange(): void {
    const t = this.currentTemplate;
    if (!this.isEditing || !this.templateId || !t) {
      this.logoUploading = false;
      return;
    }
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const request: QuoteTemplateRequest = {
      name: t.name,
      logoUrl: this.logoPreviewUrl ?? undefined,
      logoFileId: this.logoFileId ?? undefined,
      primaryColor: t.primaryColor || undefined,
      secondaryColor: t.secondaryColor || undefined,
      headerText: t.headerText || undefined,
      footerText: t.footerText || undefined,
      contactInfoText: t.contactInfoText || undefined,
      defaultTermsConditions: t.defaultTermsConditions || undefined,
      showLogo: t.showLogo,
      showWatermark: t.showWatermark,
      watermarkText: t.watermarkText || undefined,
      isDefault: t.isDefault,
      isActive: t.isActive
    };
    this.templateApi.updateTemplate(this.templateId, businessId, request).pipe(
      finalize(() => (this.logoUploading = false))
    ).subscribe({
      next: (updated) => {
        this.currentTemplate = updated;
        this.toast.updated('Logo de la plantilla');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo guardar el logo' });
      }
    });
  }

  // Open a sample PDF with this template's branding applied (only available when editing a saved template).
  previewPdf(): void {
    if (!this.templateId) return;

    this.previewing = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.templateApi.previewTemplate(this.templateId, businessId).pipe(
      finalize(() => (this.previewing = false))
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Revoke after a delay so the new tab has time to load the blob.
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo generar la vista previa' });
      }
    });
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    return 'Campo inválido';
  }
}
